import { AsyncPipe, NgTemplateOutlet } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatIconButton } from '@angular/material/button'
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInput } from '@angular/material/input'
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu'
import { MatTab, MatTabContent, MatTabGroup } from '@angular/material/tabs'
import { MatToolbar } from '@angular/material/toolbar'
import { ActivatedRoute } from '@angular/router'
import {
  ElderHeaderComponent,
  ElderMinValidator,
  ElderPaneActionsComponent,
  ElderPaneContentComponent,
  ElderPaneHeaderComponent,
  ElderPaneTitleComponent,
  ElderSelectComponent,
  ElderSinglePaneWrapperComponent,
  ElderTabGroupRoutingDirective,
  ElderThemeDirective,
  ElderToastService,
} from '@elderbyte/ngx-starter'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, Observable } from 'rxjs'
import { IdValidator } from '../../../../features/validators/id-validator'
import { FacilityContextService } from '../../../context/facility-context/facility-context.service'
import { FacilitySelectDirective } from '../../../facilities/facility-select.directive'
import { PropertyConf } from '../../../properties/conf/property-conf'
import { PropertyConfTableComponent } from '../../../properties/conf/property-conf-table/property-conf-table.component'
import { DynamicPropertyConf } from '../../../properties/dynamic/dynamic-property-conf'
import { DynamicPropertyConfBrowserComponent } from '../../../properties/dynamic/dynamic-property-conf-browser/dynamic-property-conf-browser.component'
import { PropertyUtil } from '../../../properties/property-util'
import { NumberSequence } from '../../../sequencing/number/number-sequence'
import { NumberSequenceDetailComponent } from '../../../sequencing/number/number-sequence-detail/number-sequence-detail.component'
import { WorkStation } from '../../workstations/work-station'
import { WorkStationTableComponent } from '../../workstations/work-station-table/work-station-table.component'
import { AssignmentStrategySelectDirective } from '../assignment-strategy-select.directive'
import { WorkCenterScheduleDetailComponent } from '../scheduling/work-center-schedule-detail/work-center-schedule-detail.component'
import { WorkCenterItemsNumberSequenceService } from '../sequencing/work-center-items-number-sequence.service'
import { ItemSortSpecDetailComponent } from '../types/items/item-sort-spec-detail/item-sort-spec-detail.component'
import { WorkCenterType } from '../types/work-center-type'
import { WorkCenterTypeSelectDirective } from '../types/work-center-type-select/work-center-type-select.directive'
import { WorkCenterTypeService } from '../types/work-center-type.service'
import { WorkCenter } from '../work-center'
import { WorkCenterDetailCapabilitiesComponent } from '../work-center-detail-capabilities/work-center-detail-capabilities.component'
import { WorkCenterService } from '../work-center.service'
import { WorkCenterLabelingComponent } from './work-center-labeling/work-center-labeling.component'
import { WorkCenterPackagingComponent } from './work-center-packaging/work-center-packaging.component'

@Component({
  selector: 'app-work-center-detail',
  templateUrl: './work-center-detail.component.html',
  styleUrls: ['./work-center-detail.component.scss'],
  imports: [
    MatToolbar,
    ElderSinglePaneWrapperComponent,
    ElderPaneHeaderComponent,
    ElderPaneTitleComponent,
    ElderPaneActionsComponent,
    ElderPaneContentComponent,
    ElderThemeDirective,
    MatIconButton,
    MatIcon,
    NgTemplateOutlet,
    MatTabGroup,
    ElderTabGroupRoutingDirective,
    MatTab,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    FormsModule,
    MatFormField,
    MatLabel,
    ElderSelectComponent,
    FacilitySelectDirective,
    MatInput,
    MatError,
    WorkCenterTypeSelectDirective,
    AssignmentStrategySelectDirective,
    ElderMinValidator,
    WorkStationTableComponent,
    WorkCenterPackagingComponent,
    WorkCenterLabelingComponent,
    PropertyConfTableComponent,
    DynamicPropertyConfBrowserComponent,
    WorkCenterDetailCapabilitiesComponent,
    MatTabContent,
    WorkCenterScheduleDetailComponent,
    NumberSequenceDetailComponent,
    ItemSortSpecDetailComponent,
    ElderHeaderComponent,
    AsyncPipe,
    ElderTranslatePipe,
  ],
})
export class WorkCenterDetailComponent implements OnInit {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly _workCenter$: BehaviorSubject<WorkCenter> = new BehaviorSubject(null)

  public readonly workCenterType$: BehaviorSubject<WorkCenterType> =
    new BehaviorSubject<WorkCenterType>(null)

  public readonly numberSequence$ = new BehaviorSubject<NumberSequence>(null)

  private _creationMode: boolean

  @Input()
  public embedded = false

  @Output()
  public closeRequest: EventEmitter<Event> = new EventEmitter<Event>()

  public workStations: WorkStation[] = []

  public properties$ = new BehaviorSubject<PropertyConf[]>([])

  public dynamicProperties$ = new BehaviorSubject<DynamicPropertyConf[]>([])

  public readonly idValidatorPattern = IdValidator.pattern

  private workCenterService = inject(WorkCenterService)

  private workCenterTypeService = inject(WorkCenterTypeService)

  private appContextService = inject(FacilityContextService)

  private numberSequenceService = inject(WorkCenterItemsNumberSequenceService)

  private toastService = inject(ElderToastService)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const route = inject(ActivatedRoute)
    this.workCenter = route.snapshot.data['workCenter']
  }

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnInit(): void {
    if (!this.workCenter) {
      this.workCenter = this.initNewWorkCenter()
    }
  }

  /***************************************************************************
   *                                                                         *
   * Getter / Setters                                                        *
   *                                                                         *
   **************************************************************************/

  public get workCenter$(): Observable<WorkCenter> {
    return this._workCenter$.asObservable()
  }

  public get workCenter(): WorkCenter {
    return this._workCenter$.getValue()
  }

  @Input()
  public set workCenter(workCenter: WorkCenter) {
    if (workCenter) {
      this._creationMode = !workCenter.id
      this._workCenter$.next(workCenter)

      if (!this.creationMode && workCenter.type) {
        this.workCenterTypeService.findById(workCenter.type).subscribe({
          next: (type) => {
            this.workCenterType$.next(type)

            PropertyUtil.populate(workCenter.properties, type.propertySpecs)
            this.properties$.next(workCenter.properties)

            PropertyUtil.populateDynamic(workCenter.dynamicProperties, type.dynamicPropertySpecs)
            this.dynamicProperties$.next(workCenter.dynamicProperties)
          },
          error: (err) => this.toastService.error('Could not load WorkCenter type!', err),
        })

        this.numberSequenceService.findById(workCenter.id).subscribe({
          next: (numberSequence) => (this.numberSequence = numberSequence),
          error: (err) => this.toastService.error('Could not load number sequence for WC!', err),
        })
      }
    }
  }

  public get creationMode(): boolean {
    return this._creationMode
  }

  public get deletionEnabled(): boolean {
    return !this.creationMode
  }

  public get numberSequence(): NumberSequence {
    return this.numberSequence$.getValue()
  }

  public set numberSequence(sequence: NumberSequence) {
    this.numberSequence$.next(sequence)
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public onSave(event: Event): void {
    this.save()
  }

  public requestArchival(toArchive: WorkCenter, deleteUnused: boolean): void {
    this.workCenterService.archive(toArchive, deleteUnused).subscribe({
      next: (_) => {
        this.emitClose()
        this.toastService.info('messages.action_successful')
      },
      error: (err) => this.toastService.error('messages.action_failed', err),
    })
  }

  public onClose(event: Event): void {
    this.emitClose(event)
  }

  public onNumberSequenceCreation() {
    if (this.workCenter?.id) {
      this.numberSequenceService.createNew(this.workCenter.id).subscribe({
        next: (createdNumberSequence) => {
          this.numberSequence = createdNumberSequence
          this.toastService.info('messages.persisting_successful')
        },
        error: (err) => this.toastService.error('Could not create number sequence!', err),
      })
    }
  }

  public onNumberSequenceSave(sequence: NumberSequence) {
    this.numberSequenceService.save(sequence).subscribe({
      next: (updatedSequence) => {
        this.numberSequence = updatedSequence
        this.toastService.info('messages.persisting_successful')
      },
      error: (err) => this.toastService.error('Could not save number sequence!', err),
    })
  }

  public onNumberSequenceDeletion(sequence: NumberSequence) {
    this.numberSequenceService.delete(sequence).subscribe({
      next: (_) => {
        this.toastService.info('messages.deleting_successful')
        this.numberSequence = null
      },
      error: (err) => this.toastService.error('Could not save number sequence!', err),
    })
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private save(): void {
    this.workCenter.properties = this.properties$.getValue()
    this.workCenter.dynamicProperties = this.dynamicProperties$.getValue()

    this.workCenterService.save(this.workCenter).subscribe({
      next: (wc) => {
        this.workCenter = wc
        this.toastService.info('messages.persisting_successful')
      },
      error: (err) => this.toastService.error('messages.persisting_failed', err),
    })
  }

  private emitClose(event?: Event): void {
    this.closeRequest.emit(event)
  }

  private initNewWorkCenter(): WorkCenter {
    const wc = new WorkCenter()
    const currentFacility = this.appContextService.facility
    if (currentFacility) {
      wc.facility = this.appContextService.facility.id
    }
    return wc
  }
}
